import React from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'; //-tech-debt-react-query;
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';

import { DEFAULT_QUERY_OPTIONS } from '../../../../support/constants';
import {
  apiDelete,
  apiGet,
  apiPostJson,
  apiPutJson,
} from '../../../../support/aiApi';
import { boolChip, normalizeItems, safePreview } from './TuningAdminTable';

const resourceTypeOptions = [
  { value: 'what', label: 'What' },
  { value: 'why', label: 'Why' },
  { value: 'sell', label: 'Sell' },
  { value: 'configure', label: 'Configure' },
  { value: 'validate', label: 'Validate' },
  { value: 'differentiate', label: 'Differentiate' },
  { value: 'troubleshoot', label: 'Troubleshoot' },
];

const workflowTypeOptions = [
  { value: 'entity_documentation', label: 'Entity Documentation' },
  { value: 'snackable_course', label: 'Snackable Course' },
];

function labelOf(value: any) {
  if (!value) return '-';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value.label || value.name || value.key || String(value.id || '-');
}

function idOf(value: any): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value;

  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  const n = Number(value.id ?? value.Id ?? value.value);
  return Number.isFinite(n) ? n : null;
}

function fkIdOf(value: any): string | null {
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  const id = value.id ?? value.Id ?? value.pk ?? value.value;
  return id === null || id === undefined || id === '' ? null : String(id);
}

function titleize(value: string) {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function depthIdOf(row: any): string | null {
  const value =
    row?.technical_depth ??
    row?.technical_depth_id ??
    row?.technical_depth_uuid ??
    row?.technical_depth_spec ??
    row?.technical_depth_spec_id;

  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'object') {
    const id = value.id ?? value.Id ?? value.pk ?? value.value;
    return id === null || id === undefined || id === '' ? null : String(id);
  }

  return String(value);
}

function depthFkValueOf(row: any, depthSpecs: any[]): string | null {
  const raw =
    row?.technical_depth ??
    row?.technical_depth_id ??
    row?.technical_depth_uuid ??
    row?.technical_depth_spec ??
    row?.technical_depth_spec_id;

  if (raw === null || raw === undefined || raw === '') return null;

  if (typeof raw === 'object') {
    return fkIdOf(raw);
  }

  const rawString = String(raw);

  const byId = depthSpecs.find((depth) => String(depth.id) === rawString);
  if (byId) return String(byId.id);

  const byKey = depthSpecs.find((depth) => String(depth.key) === rawString);
  if (byKey) return String(byKey.id);

  return rawString;
}

function depthLabel(row: any, depthSpecs: any[] = []) {
  const detail =
    row?.technical_depth_detail ||
    row?.technical_depth_spec ||
    (typeof row?.technical_depth === 'object' ? row.technical_depth : null);

  if (detail) {
    return detail.name || detail.label || titleize(detail.key) || String(detail.id || '-');
  }

  if (row?.technical_depth_name) {
    return row.technical_depth_name;
  }

  if (row?.technical_depth_key) {
    return titleize(row.technical_depth_key);
  }

  const depthId = depthIdOf(row);

  if (depthId) {
    const matchedDepth = depthSpecs.find((depth) => String(depth.id) === String(depthId));

    if (matchedDepth) {
      return matchedDepth.name || matchedDepth.label || titleize(matchedDepth.key);
    }

    return `Depth ${String(depthId).slice(0, 8)}...`;
  }

  return 'Resource Default';
}

const ResourceEditorDialog: React.FC<{
  open: boolean;
  entityType: any | null;
  depthSpecs: any[];
  initialValue?: any | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payloads: any[]) => Promise<void>;
}> = ({
        open,
        entityType,
        depthSpecs,
        initialValue,
        saving,
        onClose,
        onSubmit,
      }) => {
  const [form, setForm] = React.useState<any>({});
  const [workflowTypes, setWorkflowTypes] = React.useState<string[]>(['entity_documentation']);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;

    const workflow =
      initialValue?.workflow_type ||
      initialValue?.workflow_types?.[0] ||
      'entity_documentation';

    setForm({
      id: initialValue?.id,
      entity_type: idOf(initialValue?.entity_type ?? entityType?.id),
      resource_type: initialValue?.resource_type || 'what',
      workflow_type: workflow,
      label: initialValue?.label || '',
      description: initialValue?.description || '',
      technical_depth: depthFkValueOf(initialValue, depthSpecs),
      optional_inclusion_context:
        initialValue?.optional_inclusion_context ||
        initialValue?.optional_context ||
        '',
      is_required: initialValue?.is_required ?? true,
      is_active: initialValue?.is_active ?? true,
      sort_order: initialValue?.sort_order ?? 100,
      updated_by: initialValue?.updated_by || 'admin-ui',
    });

    setWorkflowTypes(
      Array.isArray(initialValue?.available_workflow_types) && initialValue.available_workflow_types.length
        ? initialValue.available_workflow_types
        : [workflow],
    );

    setError(null);
  }, [open, initialValue, entityType, depthSpecs]);

  const setField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const submit = async () => {
    setError(null);

    if (!entityType?.id && !form.entity_type) {
      setError('Entity Type is required.');
      return;
    }

    if (!form.resource_type) {
      setError('Resource Type is required.');
      return;
    }

    if (!workflowTypes.length) {
      setError('At least one workflow type is required.');
      return;
    }

    const entityTypeId = idOf(form.entity_type ?? entityType?.id);
    const technicalDepthId = fkIdOf(form.technical_depth);

    const payloads = workflowTypes.map((workflowType, idx) => ({
      ...form,
      id: idx === 0 ? form.id : undefined,
      entity_type: entityTypeId,
      workflow_type: workflowType,
      available_workflow_types: workflowTypes,

      // ForeignKey field to ai_depth_instruction_spec.
      // Must be a UUID/id string or null. Null means use AIResourceTypeSpec.default_depth_key.
      technical_depth: technicalDepthId,

      optional_inclusion_context: form.is_required ? '' : form.optional_inclusion_context,
      label: form.label || titleize(form.resource_type),
      sort_order: Number(form.sort_order || 100),
      is_required: Boolean(form.is_required),
      is_active: Boolean(form.is_active),
      updated_by: form.updated_by || 'admin-ui',
    }));

    await onSubmit(payloads);
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {form.id ? 'Edit Required Resource' : 'Add Required Resource'}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {entityType ? (
            <Alert severity="info">
              Configuring resource requirements for <b>{entityType.label || entityType.key}</b>.
            </Alert>
          ) : null}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                required
                label="Resource Type"
                value={form.resource_type || 'what'}
                onChange={(event) => setField('resource_type', event.target.value)}
              >
                {resourceTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                required
                label="Workflow Type"
                value={form.workflow_type || 'entity_documentation'}
                onChange={(event) => {
                  const value = event.target.value;
                  setField('workflow_type', value);
                  setWorkflowTypes((prev) => (prev.includes(value) ? prev : [...prev, value]));
                }}
                helperText="Primary workflow type for this row."
              >
                {workflowTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                SelectProps={{ multiple: true }}
                label="Available Workflow Types"
                value={workflowTypes}
                onChange={(event) => {
                  const value = event.target.value;
                  setWorkflowTypes(typeof value === 'string' ? value.split(',') : value as string[]);
                }}
                helperText="When adding, one resource row is created per selected workflow type."
              >
                {workflowTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={workflowTypes.includes(option.value)} />
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Technical Depth Override"
                value={form.technical_depth ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setField('technical_depth', value === '' ? null : String(value));
                }}
                helperText="Optional. Leave blank to use the Resource Type default depth."
              >
                <MenuItem value="">
                  Resource Type Default
                </MenuItem>

                {depthSpecs.map((depth) => (
                  <MenuItem key={String(depth.id)} value={String(depth.id)}>
                    {depth.name || depth.label || titleize(depth.key)} ({depth.key})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Order"
                value={form.sort_order ?? 100}
                onChange={(event) => setField('sort_order', event.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Label"
                value={form.label || ''}
                onChange={(event) => setField('label', event.target.value)}
                helperText="Optional. Defaults to the resource type label."
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Updated By"
                value={form.updated_by || 'admin-ui'}
                onChange={(event) => setField('updated_by', event.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.is_active)}
                      onChange={(event) => setField('is_active', event.target.checked)}
                    />
                  }
                  label="Active"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(form.is_required)}
                      onChange={(event) => setField('is_required', event.target.checked)}
                    />
                  }
                  label="Required"
                />
              </Stack>
            </Grid>

            {!form.is_required ? (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="Optional Inclusion Context"
                  value={form.optional_inclusion_context || ''}
                  onChange={(event) => setField('optional_inclusion_context', event.target.value)}
                  helperText="Describe when this optional resource should be generated or included."
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Description"
                value={form.description || ''}
                onChange={(event) => setField('description', event.target.value)}
                helperText="Relationship-specific instructions for this entity/resource/workflow combination."
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>

        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const RequiredResourcesPanel: React.FC = () => {
  const queryClient = useQueryClient();

  const [selectedEntityTypeId, setSelectedEntityTypeId] = React.useState<number | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingResource, setEditingResource] = React.useState<any | null>(null);

  const entityTypesQuery = useQuery<any>({
    queryKey: ['documentation-entity-type', 'required-resources-master'],
    queryFn: () =>
      apiGet<any>('db/documentation-entity-type/', {
        size: 500,
        detailed: true,
        'order-by': 'sort_order',
      }),
   ...DEFAULT_QUERY_OPTIONS,
  });

  const depthSpecsQuery = useQuery<any>({
    queryKey: ['ai-depth-instruction-spec', 'required-resources-depths'],
    queryFn: () =>
      apiGet<any>('db/ai-depth-instruction-spec/', {
        size: 500,
        detailed: true,
        'order-by': 'key',
        is_active: true,
      }),
    ...DEFAULT_QUERY_OPTIONS,
  });

  const entityTypes = normalizeItems(entityTypesQuery.data);
  const depthSpecs = normalizeItems(depthSpecsQuery.data);

  React.useEffect(() => {
    if (selectedEntityTypeId || !entityTypes.length) return;
    setSelectedEntityTypeId(Number(entityTypes[0].id));
  }, [entityTypes, selectedEntityTypeId]);

  const selectedEntityType =
    entityTypes.find((row) => String(row.id) === String(selectedEntityTypeId)) || null;

  const resourcesQuery = useQuery<any>({
    queryKey: ['documentation-required-resource-type', selectedEntityTypeId],
    queryFn: () =>
      apiGet<any>('db/documentation-required-resource-type/', {
        entity_type: selectedEntityTypeId,
        size: 500,
        detailed: true,
        depth: 1,
        'order-by': 'sort_order',
      }),
    ...DEFAULT_QUERY_OPTIONS,
    enabled: Boolean(selectedEntityTypeId),
    placeholderData: keepPreviousData,
 });

  const resources = normalizeItems(resourcesQuery.data);

  const saveMutation = useMutation<any, Error, any[]>({
    mutationFn: async (payloads) => {
      const results = [];

      for (const payload of payloads) {
        if (payload.id) {
          results.push(
            await apiPutJson<any>(`db/documentation-required-resource-type/${payload.id}/`, payload)
          );
        } else {
          results.push(await apiPostJson<any>('db/documentation-required-resource-type/', payload));
        }
      }

      return results;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documentation-required-resource-type'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-depth-instruction-spec'] });
      setEditorOpen(false);
      setEditingResource(null);
    },
  });

  const deleteMutation = useMutation<any, Error, any>({
    mutationFn: async (row) => apiDelete<any>(`db/documentation-required-resource-type/${row.id}/`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documentation-required-resource-type'] });
    },
  });

  const refresh = async () => {
    await entityTypesQuery.refetch();
    await depthSpecsQuery.refetch();
    await resourcesQuery.refetch();
  };

  const openCreate = () => {
    setEditingResource(null);
    setEditorOpen(true);
  };

  const openEdit = (resource: any) => {
    setEditingResource(resource);
    setEditorOpen(true);
  };

  const remove = async (resource: any) => {
    if (!resource?.id) return;

    if (!window.confirm(`Delete resource requirement "${resource.label || resource.resource_type}"?`)) return;

    await deleteMutation.mutateAsync(resource);
  };

  const loading =
    entityTypesQuery.isLoading ||
    entityTypesQuery.isFetching ||
    depthSpecsQuery.isLoading ||
    depthSpecsQuery.isFetching ||
    resourcesQuery.isLoading ||
    resourcesQuery.isFetching;

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', lg: 'center' }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Required Resources
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Select an entity type, then configure the resource outputs required or optionally included for each workflow.
              Optional technical depth overrides the Resource Type default depth.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={refresh}>
              Refresh
            </Button>

            <Button
              startIcon={<AddIcon />}
              variant="contained"
              onClick={openCreate}
              disabled={!selectedEntityType}
            >
              Add Resource
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {entityTypesQuery.error || depthSpecsQuery.error || resourcesQuery.error ? (
        <Alert severity="error">
          {(entityTypesQuery.error as Error)?.message ||
            (depthSpecsQuery.error as Error)?.message ||
            (resourcesQuery.error as Error)?.message ||
            'Failed to load required resource configuration.'}
        </Alert>
      ) : null}

      {loading ? <LinearProgress /> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={4} xl={3}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} mb={1}>
              Entity Types
            </Typography>

            <Stack spacing={1}>
              {entityTypes.map((entityType) => {
                const selected = String(entityType.id) === String(selectedEntityTypeId);

                return (
                  <Paper
                    key={entityType.id}
                    variant="outlined"
                    onClick={() => setSelectedEntityTypeId(Number(entityType.id))}
                    sx={{
                      p: 1.5,
                      cursor: 'pointer',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <Typography variant="body2" fontWeight={700}>
                      {entityType.label || entityType.key}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {entityType.key}
                    </Typography>

                    {entityType.description ? (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {safePreview(entityType.description, 140)}
                      </Typography>
                    ) : null}
                  </Paper>
                );
              })}

              {!entityTypes.length && !entityTypesQuery.isLoading ? (
                <Typography variant="body2" color="text.secondary">
                  No entity types found.
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={8} xl={9}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              mb={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  {selectedEntityType
                    ? `${selectedEntityType.label || selectedEntityType.key} Resources`
                    : 'Resources'}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Configured outputs for the selected entity type.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Chip size="small" variant="outlined" label={`${resources.length} resources`} />
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${resources.filter((row) => row.is_active).length} active`}
                />
              </Stack>
            </Stack>

            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 1000 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Resource</TableCell>
                    <TableCell width={190}>Workflow</TableCell>
                    <TableCell width={170}>Depth Override</TableCell>
                    <TableCell width={110}>Required</TableCell>
                    <TableCell width={90}>Active</TableCell>
                    <TableCell width={90}>Order</TableCell>
                    <TableCell width={130} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {resources.map((resource) => (
                    <TableRow key={resource.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>
                          {resource.label || titleize(resource.resource_type)}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {resource.resource_type}
                        </Typography>

                        {resource.description ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {safePreview(resource.description, 180)}
                          </Typography>
                        ) : null}

                        {!resource.is_required && (resource.optional_inclusion_context || resource.optional_context) ? (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Optional when: {safePreview(resource.optional_inclusion_context || resource.optional_context, 180)}
                          </Typography>
                        ) : null}
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={labelOf(resource.workflow_type)}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          color={depthIdOf(resource) ? 'primary' : 'default'}
                          label={depthLabel(resource, depthSpecs)}
                        />
                      </TableCell>

                      <TableCell>{boolChip(resource.is_required)}</TableCell>
                      <TableCell>{boolChip(resource.is_active)}</TableCell>
                      <TableCell>{resource.sort_order ?? '-'}</TableCell>

                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(resource)}>
                          <EditIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                          disabled={deleteMutation.isPending}
                          onClick={() => remove(resource)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {!resources.length && !resourcesQuery.isLoading ? (
              <Typography variant="body2" color="text.secondary" mt={2}>
                No required resources configured for this entity type.
              </Typography>
            ) : null}
          </Paper>
        </Grid>
      </Grid>

      <ResourceEditorDialog
        open={editorOpen}
        entityType={selectedEntityType}
        depthSpecs={depthSpecs}
        initialValue={editingResource}
        saving={saveMutation.isPending}
        onClose={() => {
          setEditorOpen(false);
          setEditingResource(null);
        }}
        onSubmit={async (payloads) => {
          await saveMutation.mutateAsync(payloads);
        }}
      />
    </Stack>
  );
};

export default RequiredResourcesPanel;